import "./form-visit.scss";
import { useFormContext } from "react-hook-form";
import { VisitFormSchema } from "@factory/form/form.rule";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import { useQyGetContract } from "@factory/query/contract.query";
import { LabelValue } from "@shared/models/label-value.interface";
import { useState } from "react";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import InputTextareaControl from "@shared/ui/hook-form/input-textarea-control/input-textarea-control";
import { Divider } from "primereact/divider";
import useAutocompletePerson from "@domain/person/autocomplete-person/autocomplete-person";
import { GetContractModel } from "@factory/model/query-model-contract";
import ImageControl from "@shared/ui/hook-form/image-control/image-control";

type InputFormSchema = VisitFormSchema;
export interface FormVisitProps {}
export function FormVisit({}: FormVisitProps) {
  const { control, setValue, watch } = useFormContext<VisitFormSchema>();
  const doctorName = watch("doctorName");
  const doctorCode = watch("person");
  const patientCode = watch("patient");
  const defaultDoctor =
    doctorName && doctorCode ? new LabelValue(doctorName, doctorCode) : null;
  const [selectedContract, setSelectedContract] =
    useState<GetContractModel | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<LabelValue | null>(
    defaultDoctor
  );

  const contractFilter = patientCode
    ? {
        patient: patientCode,
      }
    : undefined;
  const { data: responseContract } = useQyGetContract(
    "",
    9999,
    0,
    undefined,
    contractFilter
  );
  const contractOptions = (responseContract?.data || []).map(
    (item) => new LabelValue(item.service_name, item.code, item)
  );

  const handleSelect = (selectedPerson: LabelValue) => {
    if (!selectedPerson) {
      return;
    }
    setValue("person", selectedPerson.value);
  };

  const { formElement } = useAutocompletePerson(
    selectedDoctor,
    "doctor",
    "Doctor",
    "Enter and find the name of the doctor",
    true,
    handleSelect
  );

  const handleContractSelect = (contract: string) => {
    const selectedContractModel = contractOptions.find(
      (item) => item.value === contract
    );
    const selectedContractLabelValue =
      selectedContractModel?.payload as GetContractModel;

    if (!selectedContractLabelValue) {
      return;
    }

    setSelectedContract(selectedContractLabelValue);
    setSelectedDoctor(
      new LabelValue(
        `${selectedContractLabelValue.person_first_name} ${selectedContractLabelValue.person_last_name}`,
        selectedContractLabelValue.person ?? ""
      )
    );
    setValue("person", selectedContractLabelValue.person);
  };

  return (
    <div id="FormVisit" className="visit w-full grid grid-cols-1">
      <section>
        <ImageControl<InputFormSchema>
          isMultiple={true}
          control={control}
          name="photos"
          label="Picture"
          imagePreviewClass="w-32 h-32"
        />
      </section>

      <Divider />
      <section className="grid grid-cols-2">
        <div>
          <DropdownControl<InputFormSchema>
            control={control}
            name="contract"
            label="Contract"
            options={contractOptions}
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter contract"
            onSelect={handleContractSelect}
          />
          <InputDateControl<InputFormSchema>
            control={control}
            name="date"
            label="Date"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter date"
          />
          <InputDateControl<InputFormSchema>
            type="time"
            control={control}
            name="time"
            label="Time"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter time"
          />
          <div className="pb-2 w-full md:w-3/4">{formElement}</div>
        </div>
        <div>
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="description"
            label="Description"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter description"
          />
          <InputTextareaControl<InputFormSchema>
            control={control}
            name="prescription"
            label="Prescription"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter prescription"
          />
        </div>
      </section>
    </div>
  );
}

export default FormVisit;
